import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { InstallerLogo, installerBranding, appConfig } from "@/branding/Branding";
import { useSidebarCollapse } from "@/hooks/useSidebarCollapse";
import NotificationBell from "@/components/notifications/NotificationBell";
import {
    LayoutGrid, GitBranch, MessageSquare, FileText, Calendar, Cpu,
    LogOut, Menu, X, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";

/**
 * CustomerShell — responsive portal chrome for the customer side.
 *
 * Layout matrix (Phase 2A responsive polish):
 *   • <640px  (phone / iPad Slide Over): hamburger top-bar → drawer.
 *   • 640–1023px (iPad Portrait / Split View / narrow tablet): icon rail
 *     (72px) — icons only, tooltips on hover.
 *   • ≥1024px (iPad Landscape / Desktop): full expanded sidebar (240–260px).
 *
 * The sticky sidebar footer (avatar + sign-out) is always visible.
 */

const NAV = [
    { key: "dashboard",    label: "Dashboard",    icon: LayoutGrid,    to: "/dashboard",    end: true },
    { key: "journey",      label: "Journey",      icon: GitBranch,     to: "/journey" },
    { key: "messages",     label: "Messages",     icon: MessageSquare, to: "/messages" },
    { key: "documents",    label: "Documents",    icon: FileText,      to: "/documents" },
    { key: "appointments", label: "Appointments", icon: Calendar,      to: "/appointments" },
    { key: "system",       label: "My System",    icon: Cpu,           to: "/system" },
];

/** Sidebar contents. `variant` = "rail" (icons only) | "expanded" (full) | "drawer" (mobile full). */
function SidebarBody({ user, logout, navigate, onNavigate, variant = "expanded", onToggleDesktopCollapse }) {
    const initials = (user?.name || user?.email || "?")
        .split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
    const rail = variant === "rail";

    return (
        <>
            <div className={`border-b border-white/[0.08] shrink-0 ${rail ? "px-2 pt-4 pb-3" : "px-4 pt-6 pb-4"}`}>
                <div className={rail ? "flex flex-col items-center gap-2" : "flex items-start gap-2"}>
                    <Link
                        to="/dashboard"
                        onClick={onNavigate}
                        className={`inline-flex bg-white rounded-md ${rail ? "p-1.5 w-full justify-center" : "px-3 py-2 flex-1"}`}
                        data-testid="customer-sidebar-logo"
                        title={rail ? appConfig.name : undefined}
                        aria-label={appConfig.name}
                    >
                        <InstallerLogo height={rail ? 22 : 32} />
                    </Link>
                    {onToggleDesktopCollapse && (
                        <button
                            type="button"
                            onClick={onToggleDesktopCollapse}
                            data-testid="customer-sidebar-collapse-toggle"
                            aria-label={rail ? "Expand sidebar" : "Collapse sidebar"}
                            title={rail ? "Expand sidebar" : "Collapse sidebar"}
                            className={`text-white/70 hover:text-white hover:bg-white/[0.08] transition-colors rounded-md w-9 h-9 items-center justify-center shrink-0 ${
                                rail ? "hidden lg:flex" : "flex"
                            }`}
                        >
                            {rail
                                ? <PanelLeftOpen size={16} strokeWidth={1.75} />
                                : <PanelLeftClose size={16} strokeWidth={1.75} />}
                        </button>
                    )}
                </div>
                {!rail && (
                    <>
                        <div className="text-white text-base font-medium tracking-[0.02em] mt-3">{appConfig.name}</div>
                        <div className="text-white/55 text-[11px] tracking-[0.14em] uppercase mt-0.5">Customer Portal</div>
                    </>
                )}
            </div>

            <nav className={`flex-1 min-h-0 overflow-y-auto py-4 ${rail ? "px-2" : "px-2"}`}>
                {!rail && (
                    <div className="text-white/40 text-[11px] font-medium tracking-[0.14em] px-3 mb-2">YOUR PROJECT</div>
                )}
                {NAV.map((item) => (
                    <NavLink
                        key={item.key}
                        to={item.to}
                        end={item.end}
                        onClick={onNavigate}
                        data-testid={`customer-nav-${item.key}`}
                        title={rail ? item.label : undefined}
                        aria-label={item.label}
                        className={({ isActive }) =>
                            `flex items-center rounded-md transition-colors ${
                                rail
                                    ? "justify-center h-11 w-11 mx-auto my-1"
                                    : "gap-2.5 px-3 py-2.5 min-h-[44px]"
                            } ${
                                isActive
                                    ? "bg-[#3E9E2E]/22 border border-[#3E9E2E]/55 text-white shadow-[inset_2px_0_0_#3E9E2E]"
                                    : "text-white/75 hover:bg-white/[0.08] border border-transparent"
                            }`
                        }
                    >
                        <item.icon size={rail ? 18 : 16} strokeWidth={1.75} />
                        {!rail && <span className="text-sm font-medium">{item.label}</span>}
                    </NavLink>
                ))}
            </nav>

            <div
                className={`space-y-2 shrink-0 ${rail ? "p-2" : "p-3"}`}
                style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
                data-testid="customer-sidebar-footer"
            >
                {rail ? (
                    <>
                        <Link
                            to="/profile"
                            onClick={onNavigate}
                            className="w-11 h-11 mx-auto rounded-full bg-[#3E9E2E] text-white text-xs font-medium flex items-center justify-center"
                            data-testid="customer-sidebar-profile"
                            title={user?.name || "Profile"}
                            aria-label="Open your account"
                        >
                            {initials}
                        </Link>
                        <button
                            data-testid="customer-sidebar-logout"
                            onClick={() => { logout(); navigate("/"); }}
                            className="w-11 h-11 mx-auto rounded-md flex items-center justify-center text-white/85 hover:bg-white/[0.12] transition-colors"
                            aria-label="Sign out"
                            title="Sign out"
                        >
                            <LogOut size={16} strokeWidth={1.75} />
                        </button>
                    </>
                ) : (
                    <>
                        <div className="flex items-center gap-2.5 p-2 rounded-md bg-white/[0.04]">
                            <Link
                                to="/profile"
                                onClick={onNavigate}
                                className="w-9 h-9 rounded-full bg-[#3E9E2E] text-white text-xs font-medium flex items-center justify-center shrink-0"
                                data-testid="customer-sidebar-profile"
                                aria-label="Open your account"
                            >
                                {initials}
                            </Link>
                            <div className="flex-1 min-w-0">
                                <div className="text-white text-xs font-medium truncate">{user?.name || "Signed in"}</div>
                                <div className="text-white/55 text-[10px] truncate">{user?.email}</div>
                            </div>
                            <button
                                data-testid="customer-sidebar-logout"
                                onClick={() => { logout(); navigate("/"); }}
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:bg-white/[0.12] transition-colors shrink-0"
                                aria-label="Sign out"
                                title="Sign out"
                            >
                                <LogOut size={14} strokeWidth={1.75} />
                            </button>
                        </div>
                        <div className="flex items-center justify-center pt-1">
                            <NotificationBell testidPrefix="customer-notif" />
                        </div>
                        <div className="text-center text-white/30 text-[10px] tracking-[0.08em]">
                            Installed by {installerBranding.name}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default function CustomerShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [drawer, setDrawer] = useState(false);
    const { collapsed, toggle } = useSidebarCollapse();

    const railClass = collapsed
        ? "hidden sm:flex"
        : "hidden sm:flex lg:hidden";
    const expandedClass = collapsed
        ? "hidden"
        : "hidden lg:flex";

    return (
        <div className="min-h-screen bg-[#F5F5F7] sm:flex sm:items-stretch" data-testid="customer-shell" data-sidebar-collapsed={collapsed ? "true" : "false"}>
            {/* Icon rail — Split View / Portrait iPad / narrow tablet (640–1023px) OR desktop when manually collapsed */}
            <aside
                data-testid="customer-sidebar-rail"
                className={`${railClass} sm:flex-col sm:sticky sm:top-0 sm:h-screen sm:self-start w-[72px] shrink-0 border-r border-white/[0.06]`}
                style={{ background: "linear-gradient(180deg,#152244 0%,#1D2E5C 55%,#1F3A78 100%)" }}
            >
                <SidebarBody
                    user={user}
                    logout={logout}
                    navigate={navigate}
                    variant="rail"
                    onToggleDesktopCollapse={collapsed ? toggle : undefined}
                />
            </aside>

            {/* Expanded sidebar — iPad Landscape / desktop (≥1024px) when not manually collapsed */}
            <aside
                data-testid="customer-sidebar"
                className={`${expandedClass} lg:flex-col lg:sticky lg:top-0 lg:h-screen lg:self-start w-[240px] xl:w-[260px] shrink-0 border-r border-white/[0.06]`}
                style={{ background: "linear-gradient(180deg,#152244 0%,#1D2E5C 55%,#1F3A78 100%)" }}
            >
                <SidebarBody
                    user={user}
                    logout={logout}
                    navigate={navigate}
                    variant="expanded"
                    onToggleDesktopCollapse={toggle}
                />
            </aside>

            {/* Mobile top bar */}
            <div className="sm:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-[#152244] flex items-center px-4 gap-3">
                <button
                    data-testid="customer-mobile-menu"
                    onClick={() => setDrawer(true)}
                    className="w-11 h-11 -ml-2 flex items-center justify-center text-white/85 hover:text-white transition-colors"
                    aria-label="Open menu"
                >
                    <Menu size={20} strokeWidth={1.75} />
                </button>
                <div className="bg-white rounded-md px-2 py-1"><InstallerLogo height={18} /></div>
                <span className="text-white text-sm font-medium">{appConfig.name}</span>
                <div className="flex-1" />
                <button
                    onClick={() => { logout(); navigate("/"); }}
                    className="w-11 h-11 -mr-2 flex items-center justify-center text-white/85 hover:text-white transition-colors"
                    aria-label="Sign out"
                    data-testid="customer-mobile-logout"
                >
                    <LogOut size={16} strokeWidth={1.75} />
                </button>
            </div>

            {/* Mobile slide-out drawer */}
            {drawer && (
                <div className="sm:hidden fixed inset-0 z-40" data-testid="customer-mobile-drawer">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} />
                    <aside
                        className="absolute left-0 top-0 bottom-0 w-[280px] flex flex-col shadow-2xl"
                        style={{ background: "linear-gradient(180deg,#152244 0%,#1D2E5C 55%,#1F3A78 100%)" }}
                    >
                        <button
                            data-testid="customer-drawer-close"
                            onClick={() => setDrawer(false)}
                            className="absolute top-3 right-3 text-white/70 hover:text-white z-10 w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.08] transition-colors"
                            aria-label="Close menu"
                        >
                            <X size={18} strokeWidth={1.75} />
                        </button>
                        <SidebarBody
                            user={user}
                            logout={logout}
                            navigate={navigate}
                            variant="drawer"
                            onNavigate={() => setDrawer(false)}
                        />
                    </aside>
                </div>
            )}

            <main className="flex-1 min-w-0 pt-14 sm:pt-0" data-testid="customer-main">
                {children}
            </main>
        </div>
    );
}
